/* React */
import React, { useRef, useState } from "react";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { toggleCareSite } from "src/redux/features/insights-filter/insightsFilterSlice";

/* Ionic */
import { IonCheckbox, IonIcon, IonItem } from "@ionic/react";
import { chevronDown, chevronUp } from "ionicons/icons";

/* Simple Bar */
import SimpleBar from "simplebar-react";

/* Utils */
import useClickOutsideHandler from "src/utils/hooks/useClickOutsideHandler";

/* Components */
import FilterLabel from "src/components/shared/labels/filter-label/FilterLabel";

/* Stylesheet */
import styles from "./CareSiteMultiSelect.module.scss";

/* Interfaces */
import { FilterProps } from "src/shared/interfaces/insights-filter/InsightsFilter";

interface CareSiteMultiSelectProps {
  item: FilterProps<string[]>;
}

const CareSiteMultiSelect: React.FC<CareSiteMultiSelectProps> = ({ item }) => {
  const dispatch = useDispatch<AppDispatch>();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const careSiteRestrictionEnabled = useSelector(
    (state: RootState) =>
      state.settings.backendSettings.careSiteRestrictionEnabled
  );

  useClickOutsideHandler({
    ref: dropdownRef,
    onClick: setShowDropdown,
  });

  const allItems: string[] = Array.isArray(item.items) ? item.items : [];
  const selected: string[] = Array.isArray(item.value) ? item.value : allItems;

  if (!careSiteRestrictionEnabled || allItems.length <= 1) return null;

  const displayLabel =
    selected.length === allItems.length || selected.length === 0
      ? "All sites"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} sites`;

  return (
    <div className={styles.care_site_multi_select} data-testid="care-site-filter">
      <FilterLabel label="Care Sites" />
      <div ref={dropdownRef} className={styles.wrapper_container}>
        <IonItem
          data-testid="care-site-toggle"
          style={{
            borderRadius: showDropdown ? "8px 8px 0px 0px" : "42px",
          }}
          className={styles.item}
          lines="none"
          button
          onClick={() => setShowDropdown((prev) => !prev)}
        >
          <div className={styles.value}>{displayLabel}</div>
          <IonIcon
            className={styles.icon}
            icon={showDropdown ? chevronUp : chevronDown}
            slot="end"
          />
        </IonItem>

        {showDropdown && (
          <SimpleBar className={`${styles.dropdown} filter_select_scrollbar`}>
            {allItems.map((site) => {
              const isChecked = selected.includes(site);
              const isLast = isChecked && selected.length === 1;
              return (
                <IonItem
                  key={site}
                  data-testid={`care-site-option-${site}`}
                  className={styles.dropdown_item}
                  lines="none"
                  button
                  disabled={isLast}
                  onClick={() => dispatch(toggleCareSite(site))}
                >
                  <IonCheckbox
                    slot="start"
                    checked={isChecked}
                    disabled={isLast}
                    onIonChange={() => dispatch(toggleCareSite(site))}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {site}
                </IonItem>
              );
            })}
          </SimpleBar>
        )}
      </div>
    </div>
  );
};

export default CareSiteMultiSelect;
